'use client';

import { useEffect, useRef, useState } from 'react';
import { AffiliateContext, AffiliateProduct } from '@/types/affiliate';
import {
  formatProductPrice,
  isAmazonStore,
  upscaleProductImageUrl,
  withAffiliateTracking,
} from '@/utils/affiliate';
import AmazonAvailableBadge from './AmazonAvailableBadge';

interface ProductCardProps {
  product: AffiliateProduct;
  lang: string;
  seeProductLabel: string;
  /** Texto alternativo localizado del cartel "Disponible en Amazon". */
  amazonAvailableLabel: string;
  /** Si el usuario está en España (decide la variante `.es` del cartel). */
  isSpain: boolean;
  /** Página de origen, se añade al clickUrl como `ctx` para tracking. */
  ctx: AffiliateContext;
}

// Oculta la imagen si falla la carga (recomendación del proveedor).
const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.display = 'none';
};

/**
 * Reveladores pendientes por tarjeta. Un solo observer para toda la página: con
 * 7 secciones de tarjetas, uno por tarjeta sería un observer por foto.
 */
const revealers = new WeakMap<Element, () => void>();
let sharedObserver: IntersectionObserver | null = null;

const getObserver = (): IntersectionObserver | null => {
  if (typeof IntersectionObserver === 'undefined') return null;
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealers.get(entry.target)?.();
          revealers.delete(entry.target);
          sharedObserver?.unobserve(entry.target);
        }
      },
      // Margen ancho en horizontal (2-3 tarjetas por delante) para que al
      // deslizar la tira la foto ya esté pedida; estrecho en vertical, que es
      // donde está el ahorro de verdad.
      { rootMargin: '250px 700px' }
    );
  }
  return sharedObserver;
};

/**
 * Pide la foto solo cuando la tarjeta se acerca al viewport.
 *
 * `loading="lazy"` no basta dentro de una tira horizontal: Chrome considera
 * "cerca del viewport" las tarjetas que quedan fuera por la DERECHA, así que
 * descargaba casi todas las fotos de la página al abrirla. Un observer sí
 * distingue: una tarjeta desplazada en horizontal no interseca y no se pide.
 */
const useRevealOnApproach = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = getObserver();
    // Sin soporte de observer (navegador antiguo) se carga sin más: es peor no
    // ver ninguna foto que verlas todas de golpe.
    if (!observer) {
      setRevealed(true);
      return;
    }

    revealers.set(node, () => setRevealed(true));
    observer.observe(node);
    return () => {
      revealers.delete(node);
      observer.unobserve(node);
    };
  }, []);

  return { ref, revealed };
};

export default function ProductCard({
  product,
  lang,
  seeProductLabel,
  amazonAvailableLabel,
  isSpain,
  ctx,
}: ProductCardProps) {
  const brandText = product.brand || product.store || '';
  const isAmazon = isAmazonStore(product.store);
  const { ref: frameRef, revealed } = useRevealOnApproach();

  return (
    <a
      href={withAffiliateTracking(product.clickUrl, ctx)}
      target="_blank"
      rel="noopener sponsored nofollow"
      className="relative flex flex-col rounded-lg border border-border bg-card p-3 transition-all hover:-translate-y-0.5 hover:border-muted-foreground/40 hover:shadow-md"
    >
      {product.discountPct ? (
        <span className="absolute right-2.5 top-2.5 z-10 rounded-full bg-green-600 px-2 py-0.5 text-[11px] font-bold text-white">
          -{Math.round(product.discountPct)}%
        </span>
      ) : null}

      {/* Marco de foto de proporción fija.
          La imagen va ABSOLUTA a propósito: en el flujo normal, un `max-height`
          en porcentaje dentro de un contenedor cuya altura sale de `aspect-ratio`
          es una dependencia cíclica que el navegador resuelve ignorándola, así
          que la foto imponía su altura natural, estiraba el marco y descuadraba
          la fila entera (unas tarjetas con la foto mucho más alta que otras).

          `cover` sobre un marco más bajo que cuadrado recorta arriba y abajo, que
          es donde el feed deja las bandas blancas: las fotos vienen encajadas en
          un cuadrado con relleno blanco, y un producto ancho (una zapatilla)
          ocupaba media altura del marco. El 4/3 se come ~12% por arriba y ~12%
          por abajo; si alguna familia sale demasiado recortada, subir la
          proporción hacia 1/1 es el dial. */}
      <div
        ref={frameRef}
        className="relative mb-2.5 aspect-[4/3] w-full overflow-hidden rounded-md bg-white"
      >
        {revealed ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={upscaleProductImageUrl(product.imageUrl)}
            alt={product.name}
            loading="lazy"
            onError={handleImageError}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        ) : null}
      </div>

      {/* Igual que el cartel de Amazon: el hueco se reserva aunque el feed no
          traiga ni marca ni tienda, para que el nombre arranque a la misma
          altura en todas las tarjetas de la fila. */}
      <div className="min-h-[15px] text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {brandText}
      </div>

      <div className="mt-0.5 line-clamp-2 min-h-[2.6em] text-xs leading-snug text-foreground">
        {product.name}
      </div>

      <div className="mt-1.5 flex flex-wrap items-baseline gap-1">
        <span className="text-[15px] font-extrabold text-foreground">
          {formatProductPrice(product.price, product.currency, lang)}
        </span>
        {product.oldPrice ? (
          <span className="text-[11px] font-medium text-muted-foreground line-through">
            {formatProductPrice(product.oldPrice, product.currency, lang)}
          </span>
        ) : null}
      </div>

      {/* El hueco del cartel se reserva SIEMPRE, también en las fichas que no son
          de Amazon: sin eso, dentro de una misma fila el enlace "Ver producto"
          queda a alturas distintas de una tarjeta a otra. */}
      <div className="mt-2 min-h-[18px]">
        {isAmazon ? (
          <AmazonAvailableBadge
            isSpain={isSpain}
            background="auto"
            alt={amazonAvailableLabel}
          />
        ) : null}
      </div>

      <div className="mt-2 text-xs font-semibold text-primary">{seeProductLabel} →</div>
    </a>
  );
}
